<template>
  <div class="template-survey">
    <div class="template-survey__content">
      <p class="template-survey__description-text">
        以下の項目に回答をお願いします。
      </p>

      <div
        v-for="question in sortedQuestions"
        :key="question.id"
        class="template-survey__question"
        :class="{
          'template-survey__question--options':
            question.type === QUESTION_TYPE.RADIO ||
            question.type === QUESTION_TYPE.CHECKBOX,
        }"
      >
        <p class="template-survey__label">
          {{ question.question
          }}<span v-if="question.require" class="template-survey__required"
            >(必須)</span
          >
        </p>

        <SurveyQuestionText
          v-if="question.type === QUESTION_TYPE.TEXT"
          :model-value="answersMap[question.id]"
          :question="question"
          :on-change="onChangeDefault(question.id)"
        />
        <SurveyQuestionTextarea
          v-else-if="question.type === QUESTION_TYPE.TEXTAREA"
          :model-value="answersMap[question.id]"
          :question="question"
          :on-change="onChangeDefault(question.id)"
        />
        <SurveyQuestionSelect
          v-else-if="question.type === QUESTION_TYPE.SELECT"
          :model-value="answersMap[question.id]"
          :question="question"
          :on-change="onChangeDefault(question.id)"
        />
        <SurveyQuestionRadio
          v-else-if="question.type === QUESTION_TYPE.RADIO"
          :model-value="answersMap[question.id]"
          :question="question"
          :on-change="onChangeDefault(question.id)"
        />
        <SurveyQuestionCheckbox
          v-else-if="question.type === QUESTION_TYPE.CHECKBOX"
          :model-value="answersMap[question.id]"
          :question="question"
          :on-change="onChangeCheckbox(question.id)"
        />

        <p v-if="errors[question.id]" class="template-survey__validation-error">
          ※必須項目です。回答をお願いします。
        </p>
        <p
          v-else-if="backendValidationError"
          class="template-survey__validation-error"
        >
          ※誤りがあります。正しい番号を入力してください。
        </p>
      </div>

      <div v-if="termsUrl" class="template-survey__terms-section">
        <section class="template-survey__terms">
          <iframe
            :src="termsUrl"
            class="template-survey__terms-iframe"
            title="利用規約"
          ></iframe>
        </section>

        <div class="template-survey__agree">
          <label class="template-survey__agree-label">
            <SurveyCheckbox :checked="isAgreed" :on-change="toggleAgreed" />
            <span class="template-survey__agree-text"
              >個人情報の取扱いに同意する</span
            >
          </label>
        </div>
      </div>
    </div>

    <div class="template-survey__footer">
      <p
        v-if="hasValidationError && showSummaryError"
        class="template-survey__submit-error"
      >
        未回答の項目がありますのでご確認ください。
      </p>
      <button
        class="template-survey__submit"
        :class="{ 'template-survey__submit--active': enableSubmitButton }"
        :disabled="!enableSubmitButton"
        @click="handleSubmit"
      >
        次へ<img
          v-show="enableSubmitButton"
          src="@/assets/img/icon/svg/arrow-right-white.svg"
          width="14"
          height="14"
          class="template-survey__submit-arrow"
        />
      </button>
    </div>
  </div>
</template>

<script>
import { computed } from "vue";
import SurveyQuestionText from "@/views/Components/Molecules/TicketStore/Survey/SurveyQuestionText.vue";
import SurveyQuestionTextarea from "@/views/Components/Molecules/TicketStore/Survey/SurveyQuestionTextarea.vue";
import SurveyQuestionSelect from "@/views/Components/Molecules/TicketStore/Survey/SurveyQuestionSelect.vue";
import SurveyQuestionRadio from "@/views/Components/Molecules/TicketStore/Survey/SurveyQuestionRadio.vue";
import SurveyQuestionCheckbox from "@/views/Components/Molecules/TicketStore/Survey/SurveyQuestionCheckbox.vue";
import SurveyCheckbox from "@/views/Components/Atoms/TicketStore/Survey/SurveyCheckbox.vue";
import { useTermsAgreement } from "@/composables/useTermsAgreement";
import { useSurveyAnswers } from "@/composables/useSurveyAnswers";
import { QUESTION_TYPE } from "@/services/survey";

export default {
  name: "TemplateSurvey",
  components: {
    SurveyCheckbox,
    SurveyQuestionText,
    SurveyQuestionTextarea,
    SurveyQuestionSelect,
    SurveyQuestionRadio,
    SurveyQuestionCheckbox,
  },
  props: {
    masterSurvey: {
      type: Object,
      required: true,
    },
    termsUrl: {
      type: String,
      default: null,
    },
    backendValidationError: {
      type: Boolean,
      default: false,
    },
    showSummaryError: {
      type: Boolean,
      default: true,
    },
  },
  emits: ["submit"],
  setup(props, { emit }) {
    const { isAgreed, toggleAgreed } = useTermsAgreement();
    const {
      sortedQuestions,
      answers,
      errors,
      hasValidationError,
      validate,
      onChangeCheckbox,
      onChangeDefault,
    } = useSurveyAnswers(props.masterSurvey?.question);

    // 利用規約(termsUrl)が設定されていない場合は同意チェック自体を表示しないため、送信は最初から許可する
    const enableSubmitButton = computed(
      () => !props.termsUrl || isAgreed.value
    );

    const handleSubmit = () => {
      if (!validate()) {
        return;
      }
      emit("submit", { ...answers });
    };

    return {
      answersMap: answers,
      errors,
      sortedQuestions,
      isAgreed,
      toggleAgreed,
      enableSubmitButton,
      hasValidationError,
      QUESTION_TYPE,
      validate,
      onChangeCheckbox,
      onChangeDefault,
      handleSubmit,
    };
  },
};
</script>

<style lang="scss" scoped>
.template-survey {
  background-color: #ffffff;
  padding-top: calc(
    var(--survey-header-height) + env(safe-area-inset-top, 0px)
  );
  min-height: 100vh;

  &__content {
    padding: 20px 14px;
    padding-bottom: calc(110px + env(safe-area-inset-bottom, 0px));
    display: flex;
    flex-direction: column;
    gap: 30px;
  }

  &__description-text {
    font-size: 13px;
    font-weight: 400;
    color: #000000;
    line-height: 1.19;
    letter-spacing: 0.4px;
  }

  &__question {
    display: flex;
    flex-direction: column;
    gap: 7px;

    &--options {
      gap: 16px;
    }
  }

  &__label {
    font-size: 13px;
    font-weight: 700;
    color: #000000;
    line-height: 1.2;
    letter-spacing: -0.4px;
  }

  &__required {
    color: #eb5757;
    font-size: 13px;
    font-weight: 700;
    margin-left: 7px;
  }

  &__validation-error {
    font-size: 13px;
    font-weight: 700;
    color: #eb5757;
    letter-spacing: -0.4px;
  }

  &__terms-section {
    display: flex;
    flex-direction: column;
    gap: 16px;
    border-top: 1px solid #e0e0e0;
    padding-top: 16px;
  }

  &__terms {
    border: 1px solid #000000;
  }

  &__terms-iframe {
    width: 100%;
    height: 140px;
    border: none;
  }

  &__agree {
    display: flex;
    justify-content: center;
  }

  &__agree-label {
    display: flex;
    align-items: center;
    gap: 6px;
    width: fit-content;
    cursor: pointer;
    font-size: 14px;
    font-weight: 700;
    color: #000000;
    letter-spacing: -0.4px;
  }

  &__submit-error {
    font-size: 13px;
    font-weight: 700;
    color: #eb5757;
    letter-spacing: -0.4px;
    margin-bottom: 8px;
    text-align: center;
  }

  &__footer {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    background-color: #ffffff;
    padding: 16px 14px;
    padding-bottom: calc(16px + env(safe-area-inset-bottom, 0px));
  }

  &__submit {
    width: 100%;
    height: 44px;
    background-color: #e9e9e9;
    color: #999999;
    font-size: 13px;
    font-weight: 700;
    letter-spacing: -0.4px;
    border: none;
    border-radius: 70px;
    cursor: not-allowed;
    -webkit-appearance: none;
    appearance: none;
    position: relative;
    text-align: center;

    &--active {
      background-color: #f4b63f;
      color: #ffffff;
      cursor: pointer;
    }

    &-arrow {
      position: absolute;
      right: 14px;
      top: 50%;
      transform: translateY(-50%);
    }
  }
}
</style>
